import { X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { DocumentPreview } from './DocumentPreview'
import { type PreviewDocument, previewDocumentKey } from './previewDocument'

interface Props {
  docs: PreviewDocument[]
  onClose: () => void
  className?: string
}

/**
 * Every document this conversation produced, beside the chat, so staff can see
 * the agent's work land before asking for a correction. A turn that fills two
 * forms shows both, stacked in the order the agent filled them. Read-only —
 * corrections happen by asking the agent again, never by editing here.
 */
export function DocumentPreviewPane({ docs, onClose, className }: Props) {
  // A lone document fills the pane; stacked ones each get a tall viewer and the column scrolls.
  const isSingle = docs.length === 1

  return (
    // Same vertical rhythm as the chat column next door, so the two headers line up.
    <div className={cn('bg-muted/30 flex h-full min-w-0 flex-col gap-3 border-r px-5 py-6', className)}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold">{isSingle ? 'Document' : `${docs.length} documents`}</span>
        <Button size="icon-sm" variant="ghost" onClick={onClose} aria-label="Close document preview">
          <X className="size-4" />
        </Button>
      </div>

      <div className={cn('min-h-0 flex-1', isSingle ? 'flex flex-col' : 'space-y-6 overflow-y-auto')}>
        {docs.map((doc) => (
          <DocumentPreview key={previewDocumentKey(doc)} doc={doc} stacked={!isSingle} />
        ))}
      </div>
    </div>
  )
}
